import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import {
  CircleCheckIcon,
  PaperclipIcon,
  PlusIcon,
  SquarePenIcon,
  TagsIcon,
} from "lucide-react";
import { Tooltip } from "@/components/Common/Tooltip";
import { cn } from "@/lib/utils";

export type ActivityTab = "notes" | "tasks" | "files" | "tags";
export const ACTIVITY_TABS: ActivityTab[] = ["notes", "tasks", "files", "tags"];

export interface ActivityCounts {
  notes: number;
  tasksTotal: number;
  tasksOpen: number;
  files: number;
  tags: number;
}

export type SegmentState = "active" | "done" | "plain" | "starred";

export const formatCount = (count: number) => (count > 99 ? "99+" : `${count}`);

/** The number a tab chip shows: tasks count OPEN tasks. */
export const activityCount = (tab: ActivityTab, c: ActivityCounts) =>
  tab === "notes"
    ? c.notes
    : tab === "files"
      ? c.files
      : tab === "tags"
        ? c.tags
        : c.tasksOpen;

/**
 * "Active" = something to look at. A card whose tasks are all complete is
 * still active (green check); only a card with no tasks at all is not.
 */
export const isActivityActive = (tab: ActivityTab, c: ActivityCounts) =>
  tab === "notes"
    ? c.notes > 0
    : tab === "files"
      ? c.files > 0
      : tab === "tags"
        ? c.tags > 0
        : c.tasksTotal > 0;

/** Accessible name / tooltip of an ACTIVE tab's segment. */
export const activityLabel = (tab: ActivityTab, c: ActivityCounts) => {
  if (tab === "notes") return `Notes (${formatCount(c.notes)})`;
  if (tab === "files") return `Files (${formatCount(c.files)})`;
  if (tab === "tags") return `Tags (${formatCount(c.tags)})`;
  if (c.tasksOpen === 0) return "All tasks complete";
  return `Tasks: ${formatCount(c.tasksOpen)} open`;
};

export const activityState = (
  tab: ActivityTab,
  c: ActivityCounts
): SegmentState =>
  tab === "tasks" && c.tasksTotal > 0 && c.tasksOpen === 0 ? "done" : "active";

const ADD_NOUNS: Record<ActivityTab, string> = {
  notes: "note",
  tasks: "task",
  files: "file",
  tags: "tag",
};

/** Label of the `+` cell: lists only what can still be added. */
export const addLabel = (inactive: ActivityTab[]) => {
  const nouns = inactive.map((tab) => ADD_NOUNS[tab]);
  const list =
    nouns.length > 1
      ? `${nouns.slice(0, -1).join(", ")} or ${nouns[nouns.length - 1]}`
      : (nouns[0] ?? "");
  return `Add ${list}`;
};

/** The button never renders more than this many cells (the + counts). */
export const MAX_ACTIVITY_CELLS = 3;

export interface VisibleCells {
  /** Active tabs that get a segment, in tab order (Tags last). */
  segments: ActivityTab[];
  showAdd: boolean;
}

/**
 * Which cells fit. Priority: Notes/Tasks/Files first, then the + (while
 * something can still be added), then Tags with whatever is left. A dropped
 * tab stays reachable through the popover's tab strip.
 */
export const visibleCells = (
  tabs: ActivityTab[],
  counts: ActivityCounts,
  disabled = false
): VisibleCells => {
  const active = tabs.filter((tab) => isActivityActive(tab, counts));
  const hasInactive = active.length < tabs.length;
  const primary = active.filter((tab) => tab !== "tags");
  const free = MAX_ACTIVITY_CELLS - primary.length;
  const showAdd = hasInactive && !disabled && free > 0;
  const showTags = active.includes("tags") && free - (showAdd ? 1 : 0) > 0;
  return { segments: showTags ? [...primary, "tags"] : primary, showAdd };
};

const STATE_CLASSES: Record<SegmentState, string> = {
  active:
    "bg-transparent text-[#475569] hover:bg-black/10 group-data-[overdue]/activity:text-white group-data-[overdue]/activity:hover:bg-white/20",
  done: "bg-[#dcfce7] text-[#15803d]",
  plain: "bg-transparent",
  starred: "bg-[#fbbf24] text-[#1e293b]",
};

export interface ActivitySegmentProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Accessible name AND hover tooltip text. */
  label: string;
  state: SegmentState;
  children: ReactNode;
}

/**
 * One cell of the activity button. Forwards its ref and spreads unknown props
 * onto the <button> so Radix `PopoverTrigger asChild` can drive it (the color
 * segment). Never style it off `data-state`: Tooltip's asChild trigger
 * overwrites a Popover trigger's `data-state` on the same element.
 */
export const ActivitySegment = forwardRef<
  HTMLButtonElement,
  ActivitySegmentProps
>(
  (
    {
      label,
      state,
      className,
      children,
      disabled,
      onMouseDown,
      onClick,
      ...rest
    },
    ref
  ) => (
    <Tooltip message={label} delayDuration={150}>
      {/* Disabled is aria-disabled + a swallowed click, NOT the native
          attribute: Common/Tooltip wraps a natively-disabled button in a plain
          <div>, which would un-stretch the cell (icon stuck at the top). The
          separators live on the container's divide-x for the same reason. */}
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-disabled={disabled || undefined}
        data-disabled={disabled ? "" : undefined}
        className={cn(
          "flex shrink-0 cursor-pointer items-center gap-px self-stretch px-0.5 text-[10px] font-bold leading-none data-[disabled]:cursor-default data-[disabled]:opacity-50",
          STATE_CLASSES[state],
          className
        )}
        // dnd-kit starts a drag from pointer/mouse down anywhere on the card.
        onMouseDown={(e) => {
          e.stopPropagation();
          onMouseDown?.(e);
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) onClick?.(e);
        }}
        {...rest}
      >
        {children}
      </button>
    </Tooltip>
  )
);
ActivitySegment.displayName = "ActivitySegment";

const ICONS = {
  notes: SquarePenIcon,
  tasks: CircleCheckIcon,
  files: PaperclipIcon,
  tags: TagsIcon,
} as const;

export interface ActivityButtonProps {
  /** Every ENABLED tab, active or not; the button decides what to render. */
  tabs: ActivityTab[];
  counts: ActivityCounts;
  overdue: boolean;
  /** Read-only card: no `+`; segments that render stay clickable. */
  disabled?: boolean;
  /**
   * Hide the Notes number (icon only) to give the footer's hours pill room.
   * `Card.Footer` decides; the number's width stays measurable through an
   * invisible measurer that is always rendered for 2+ notes.
   */
  compactNotes?: boolean;
  onSegmentClick: (tab: ActivityTab) => void;
  /** Called with the first inactive enabled tab (Notes, Tasks, Files, Tags). */
  onAddClick: (firstInactiveTab: ActivityTab) => void;
  /** The tab whose popover panel is currently open (drives aria-expanded). */
  expandedTab?: ActivityTab;
  /** Trailing slot: the color swatch segment or the star segment. */
  children?: ReactNode;
}

export const ActivityButton = forwardRef<HTMLDivElement, ActivityButtonProps>(
  (
    {
      tabs,
      counts,
      overdue,
      disabled,
      compactNotes,
      onSegmentClick,
      onAddClick,
      expandedTab,
      children,
    },
    ref
  ) => {
    const inactiveTabs = tabs.filter((tab) => !isActivityActive(tab, counts));
    const { segments, showAdd } = visibleCells(tabs, counts, disabled);
    // The popover can sit on a tab that has no segment (reached through the +
    // or the popover's own tab strip).
    const addExpanded =
      expandedTab !== undefined && !segments.includes(expandedTab);
    return (
      <div
        ref={ref}
        role="group"
        aria-label="Card activity"
        data-overdue={overdue ? "" : undefined}
        className={cn(
          "group/activity ml-auto flex h-3.5 shrink-0 items-stretch divide-x divide-[#94a3b8] overflow-hidden rounded-[3px] border border-[#94a3b8] bg-white/50",
          "data-[overdue]:divide-white/70 data-[overdue]:border-white/70 data-[overdue]:bg-white/[.12]"
        )}
      >
        {showAdd && (
          <ActivitySegment
            data-activity-tab-segment=""
            data-activity-add=""
            aria-haspopup="dialog"
            aria-expanded={addExpanded}
            label={addLabel(inactiveTabs)}
            state="active"
            onClick={() => onAddClick(inactiveTabs[0])}
          >
            <PlusIcon size={12} strokeWidth={2.2} />
          </ActivitySegment>
        )}
        {segments.map((tab) => {
          const Icon = ICONS[tab];
          const state = activityState(tab, counts);
          return (
            <ActivitySegment
              key={tab}
              data-activity-tab-segment=""
              data-activity-tab={tab}
              aria-haspopup="dialog"
              aria-expanded={expandedTab === tab}
              label={activityLabel(tab, counts)}
              state={state}
              onClick={() => onSegmentClick(tab)}
            >
              <Icon size={12} strokeWidth={state === "done" ? 2.6 : 2.2} />
              {tab === "notes" && counts.notes >= 2 && (
                <>
                  {!compactNotes && <span>{formatCount(counts.notes)}</span>}
                  {/* Same text, invisible and out of flow (no DOM text): lets
                      the footer know the number's width even while it is
                      hidden. Inherits the cell's 10px/700 type. */}
                  <span
                    aria-hidden="true"
                    data-notes-count-measure=""
                    data-text={formatCount(counts.notes)}
                    className="pointer-events-none invisible absolute after:content-[attr(data-text)]"
                  />
                </>
              )}
            </ActivitySegment>
          );
        })}
        {children}
      </div>
    );
  }
);
ActivityButton.displayName = "ActivityButton";
